import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Warehouse,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Package,
  Truck,
  AlertTriangle,
  Receipt,
  ClipboardCheck,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ArrowRightLeft,
  ClipboardEdit,
  FileText,
  KeyRound,
  Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';

const menuItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/warehouse'
  },
  {
    title: 'Stock Management',
    icon: Package,
    children: [
      { title: 'Current Stock', path: '/warehouse/stock', icon: Package },
      { title: 'All Products', path: '/warehouse/products', icon: FileText },
      { title: 'Stock Adjustments', path: '/warehouse/stock/adjustments', icon: ClipboardEdit },
      { title: 'Damaged Stock', path: '/warehouse/stock/damaged', icon: AlertTriangle }
    ]
  },
  {
    title: 'Stock Requests',
    icon: FileText,
    children: [
      { title: 'Raise Request', path: '/warehouse/stock-requests/new', icon: Plus },
      { title: 'Request History', path: '/warehouse/stock-requests', icon: FileText }
    ]
  },
  {
    title: 'Stock Receiving',
    icon: ClipboardCheck,
    children: [
      { title: 'Pending Verification', path: '/warehouse/grn/verify', icon: ClipboardCheck }
    ]
  },
  {
    title: 'Stock Transfers',
    icon: Truck,
    children: [
      { title: 'Pending Requests', path: '/warehouse/transfers/pending', icon: Package },
      { title: 'Approved Requests', path: '/warehouse/transfer-orders', icon: ClipboardCheck },
      { title: 'Ready to Dispatch', path: '/warehouse/transfers/dispatch', icon: ArrowRightLeft },
      { title: 'In Transit', path: '/warehouse/transfers/transit', icon: Truck },
      { title: 'Completed', path: '/warehouse/transfers/completed', icon: FileText },
      { title: 'Returns', path: '/warehouse/transfers/returns', icon: ArrowRightLeft },
      { title: 'Create Transfer', path: '/warehouse/transfers/new', icon: Plus }
    ]
  },
  {
    title: 'Expenses',
    icon: Receipt,
    path: '/warehouse/expenses'
  },
  {
    title: 'Reports',
    icon: BarChart3,
    children: [
      { title: 'Stock Report', path: '/warehouse/reports/stock', icon: Package },
      { title: 'Movement Report', path: '/warehouse/reports/movements', icon: ArrowRightLeft },
      { title: 'Expense Report', path: '/warehouse/reports/expenses', icon: Receipt }
    ]
  }
];

const WarehouseLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['Stock Management', 'GRN & Receiving']);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = (title) => {
    setExpandedMenus(prev =>
      prev.includes(title)
        ? prev.filter(t => t !== title)
        : [...prev, title]
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;
  const isParentActive = (children) => children?.some(child => location.pathname.startsWith(child.path));

  const renderMenuItem = (item, mobile = false) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.title);
    const active = item.path ? isActive(item.path) : isParentActive(item.children);

    if (hasChildren) {
      return (
        <div key={item.title}>
          <button
            onClick={() => toggleMenu(item.title)}
            className={cn(
              'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
              active
                ? 'bg-teal-100 text-teal-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            )}
          >
            <div className="flex items-center gap-3">
              <Icon className="w-5 h-5" />
              {(sidebarOpen || mobile) && <span>{item.title}</span>}
            </div>
            {(sidebarOpen || mobile) && (
              isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
            )}
          </button>
          {(sidebarOpen || mobile) && isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children.map(child => {
                const ChildIcon = child.icon;
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={() => mobile && setMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                      isActive(child.path)
                        ? 'bg-teal-500 text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )}
                  >
                    {ChildIcon && <ChildIcon className="w-4 h-4" />}
                    <span>{child.title}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => mobile && setMobileMenuOpen(false)}
        className={cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
          active
            ? 'bg-teal-500 text-white'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        )}
      >
        <Icon className="w-5 h-5" />
        {(sidebarOpen || mobile) && <span>{item.title}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="bg-white text-gray-700 hover:bg-gray-100 shadow-md"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/20" onClick={() => setMobileMenuOpen(false)}>
          <div
            className="w-64 h-full bg-white border-r border-gray-200 p-4 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-8 mt-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-200">
                <Warehouse className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800">Warehouse</h1>
                <p className="text-xs text-gray-500">{user?.entity_name}</p>
              </div>
            </div>
            <nav className="space-y-1">
              {menuItems.map(item => renderMenuItem(item, true))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div
        className={cn(
          'hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col border-r border-gray-200 bg-white transition-all duration-300',
          sidebarOpen ? 'lg:w-64' : 'lg:w-20'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-200">
              <Warehouse className="w-5 h-5 text-white" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="text-lg font-bold text-gray-800">Warehouse</h1>
                <p className="text-xs text-gray-500 truncate max-w-32">{user?.entity_name}</p>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          >
            {sidebarOpen ? <ChevronRight className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map(item => renderMenuItem(item))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-gray-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <Avatar className="w-9 h-9">
                  <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-500 text-white text-sm">
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                {sidebarOpen && (
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-800 truncate">{user?.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.role_name}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white border-gray-200">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-gray-800">{user?.full_name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-gray-200" />
              <DropdownMenuItem
                onClick={() => setChangePasswordOpen(true)}
                className="cursor-pointer"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-200" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <div className={cn(
        'transition-all duration-300',
        sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
      )}>
        <main className="min-h-screen">
          {children}
        </main>
      </div>
      
      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={changePasswordOpen} 
        onOpenChange={setChangePasswordOpen} 
      />
    </div>
  );
};

export default WarehouseLayout;
